import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { Button } from "@/components/ui/button";
import { FileText, MessageSquare, ArrowRight } from "lucide-react";

const AdminNotificationsPage = () => {
  const { quotes, contacts, unreadCount, markAllRead } = useAdmin();
  const initialUnread = useRef(unreadCount);

  useEffect(() => {
    markAllRead();
  }, [markAllRead]);

  const feed = useMemo(() => {
    const items = [
      ...quotes.map((q) => ({
        id: q.id,
        type: "devis",
        name: q.data?.name || `${q.data?.first_name || ""} ${q.data?.last_name || ""}`.trim(),
        date: q.created_at,
        status: q.status || "nouveau",
      })),
      ...contacts.map((c) => ({
        id: c.id,
        type: "contact",
        name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
        date: c.created_at,
      })),
    ].sort((a, b) => new Date(b.date) - new Date(a.date));

    return items.map((item, idx) => ({ ...item, isNew: idx < initialUnread.current }));
  }, [quotes, contacts]);

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{feed.length} entrées au total</p>
        {unreadCount > 0 && (
          <Button variant="outline" className="rounded-full text-sm" onClick={markAllRead}>
            Tout marquer comme lu
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {feed.map((item) => (
          <Link
            key={`${item.type}-${item.id}`}
            to={`/admin/${item.type === "devis" ? "devis" : "contacts"}/${item.id}`}
            className={`flex items-center gap-3 p-4 rounded-2xl border transition-colors hover:bg-slate-50 ${
              item.isNew ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-slate-200"
            }`}
            data-testid={`notif-${item.id}`}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${item.type === "devis" ? "bg-brand-green/10" : "bg-brand-blue/10"}`}>
              {item.type === "devis"
                ? <FileText className="h-4 w-4 text-brand-green" />
                : <MessageSquare className="h-4 w-4 text-brand-blue" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-slate-900 truncate">{item.name || "—"}</p>
                {item.isNew && (
                  <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded-full flex-shrink-0">Nouveau</span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                {item.type === "devis" ? "Devis" : "Contact"} · {new Date(item.date).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
          </Link>
        ))}
        {feed.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
            Aucune activité enregistrée.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotificationsPage;
